import type { ChangeEventHandler } from 'react';
import { memo, useCallback } from 'react';

import type { KcContext } from 'keycloakify/login/KcContext';

import { Button } from '@tripleten/design-system/button';

import { Input } from 'src/shared/components/input/input.js';
import { useFieldMessage } from 'src/shared/hooks/use-field-message.js';
import { useFormValidator } from 'src/shared/hooks/use-form-validator.js';
import { useSessionStorageState } from 'src/shared/hooks/use-session-storage-state.js';
import { internalPasswordLoginRestricted } from 'src/shared/utils/internal-password-login-restricted.js';
import { useGetMsg } from 'src/shared/utils/kc-messages.js';

import { cnPageInternalResetPassword } from './page-internal-reset-password.constants.js';

import './page-internal-reset-password.css';

import './__form/page-internal-reset-password__form.css';
import './__title/page-internal-reset-password__title.css';

export interface PageInternalResetPasswordProps {
    kcContext: KcContext.LoginResetPassword;
}

export const PageInternalResetPassword = memo<PageInternalResetPasswordProps>(({ kcContext }) => {
    const [username = '', setUsername] = useSessionStorageState<string>('username');

    const { msgStr, advancedMsgStr } = useGetMsg(kcContext);

    const resetAvailable = !internalPasswordLoginRestricted(username);

    const onUsernameChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
        (e) => setUsername(e.target.value),
        [setUsername],
    );

    const emailError = useFieldMessage(['email', 'username'], kcContext);
    const [formErrors, resetFieldError, onSubmitValidated, submitDisabled] = useFormValidator(['username'], {
        username: emailError,
    });

    return (
        <div className={cnPageInternalResetPassword()}>
            <h1 className={cnPageInternalResetPassword('title')}>{msgStr('resetPasswordTitle')}</h1>
            <form
                action={kcContext.url.loginAction}
                method="post"
                className={cnPageInternalResetPassword('form')}
                onSubmit={onSubmitValidated}
            >
                <Input
                    type="email"
                    id="username"
                    name="username"
                    defaultValue={username}
                    label={msgStr('email')}
                    autoComplete="email"
                    className={cnPageInternalResetPassword('username')}
                    onChange={onUsernameChange}
                    onFocus={resetFieldError}
                    errorMessage={advancedMsgStr(formErrors['email'] ?? '')}
                />

                <Button
                    className={cnPageInternalResetPassword('submit')}
                    variant="solid"
                    type="submit"
                    size="4"
                    disabled={submitDisabled || !resetAvailable}
                >
                    {msgStr('resetPasswordButton')}
                </Button>
            </form>

            <Button asChild className={cnPageInternalResetPassword('back-to-login')} variant="ghost" size="3">
                <a href={kcContext.url.loginUrl}>{msgStr('backToLogin')}</a>
            </Button>
        </div>
    );
});
